import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useTextGeneration } from '@/hooks/text-generation/useTextGeneration';
import { 
  EntityCreationResearchContextType, 
  TempAttachedResearch, 
  SummaryState,
  EntityCreationConfig,
  EntityCreationResearchSystem
} from './types';
import { getEntityCreationConfig } from './configs/entity-configs';
import { createBatchAttachHook } from '../../hooks/universal-creation-research/use-universal-batch-attach';

// Cache for created systems to avoid recreating contexts
const createdSystems: Record<string, EntityCreationResearchSystem> = {};

export const createEntityCreationResearchSystem = (entityType: string): EntityCreationResearchSystem => {
  // Return cached system if already created
  if (createdSystems[entityType]) {
    return createdSystems[entityType];
  }

  const config = getEntityCreationConfig(entityType);

  // Create entity-specific context
  const EntityCreationContext = createContext<EntityCreationResearchContextType | undefined>(undefined);

  // Create entity-specific hook
  const useEntityCreationResearch = () => {
    const context = useContext(EntityCreationContext);
    if (!context) {
      throw new Error(`useEntityCreationResearch must be used within ${config.displayName}CreationProvider`);
    }
    return context;
  };

  // Create entity-specific provider
  const EntityCreationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedResearch, setSelectedResearch] = useState<TempAttachedResearch[]>([]);
    const [summaryStates, setSummaryStates] = useState<Record<string, SummaryState>>({});
    const [summaryData, setSummaryData] = useState<Record<string, string>>({});
    
    const { generateText } = useTextGeneration();

    // Generate research summary for entity context
    const generateResearchSummary = useCallback(async (research: TempAttachedResearch): Promise<string> => {
      const message = `Analyze this research content and create a concise summary focused on key insights that would be valuable for ${config.entityType} content creation:

RESEARCH TITLE: ${research.research_library.title}

RESEARCH CONTENT:
${research.research_library.content}

Please provide a focused summary that highlights:
1. Main findings and key points
2. Actionable insights
3. Relevant data or statistics
4. How this relates to ${config.entityType} content strategy

Keep the summary concise but comprehensive (200-400 words).`;

      const result = await generateText({
        message,
        assistantId: 'basic', // Use basic assistant for summarization
      });

      return result;
    }, [generateText]);

    // Add research and trigger summarization (exact same logic as workbook)
    const addResearch = useCallback(async (researchLibraryItem: any): Promise<void> => {
      return new Promise<void>((resolve) => {
        const researchId = researchLibraryItem.id;
        
        console.log(`[${config.displayName}CreationContext] Adding research:`, researchLibraryItem.title, 'ID:', researchId);
        
        // Check if already selected
        if (selectedResearch.some(r => r.id === researchId)) {
          console.log(`[${config.displayName}CreationContext] Research already selected, skipping:`, researchId);
          toast.error('Research already selected');
          resolve(); // Always resolve
          return;
        }

        // Check limit
        if (selectedResearch.length >= config.researchLimit) {
          console.log(`[${config.displayName}CreationContext] Research limit reached, skipping:`, researchId);
          toast.error(`Maximum ${config.researchLimit} research items allowed`);
          resolve(); // Always resolve
          return;
        }

        // Create temporary attachment
        const tempAttachment: TempAttachedResearch = {
          id: researchId,
          research_library: {
            id: researchLibraryItem.id,
            title: researchLibraryItem.title,
            content: researchLibraryItem.content,
            research_type: researchLibraryItem.research_type,
            created_at: researchLibraryItem.created_at,
          },
          created_at: new Date().toISOString(),
        };

        // Add to selected research
        setSelectedResearch(prev => [...prev, tempAttachment]);
        
        // Set summary state to generating
        setSummaryStates(prev => ({ ...prev, [researchId]: 'generating' }));
        
        toast.success('Research selected - generating summary...');
        
        // Resolve immediately so modal can close
        resolve();

        // Generate summary in background (don't await this)
        generateResearchSummary(tempAttachment)
          .then(summary => {
            // Store summary
            setSummaryData(prev => ({ ...prev, [researchId]: summary }));
            setSummaryStates(prev => ({ ...prev, [researchId]: 'completed' }));
            toast.success('Research summary generated');
          })
          .catch(error => {
            console.error('Error generating research summary:', error);
            setSummaryStates(prev => ({ ...prev, [researchId]: 'error' }));
            toast.error('Failed to generate research summary');
          });
      });
    }, [selectedResearch, generateResearchSummary]);

    // Remove research
    const removeResearch = useCallback((researchId: string) => {
      setSelectedResearch(prev => prev.filter(r => r.id !== researchId));
      setSummaryStates(prev => {
        const newStates = { ...prev };
        delete newStates[researchId];
        return newStates;
      });
      setSummaryData(prev => {
        const newData = { ...prev };
        delete newData[researchId];
        return newData;
      });
      toast.success('Research removed');
    }, []);

    // Reset all research state
    const resetResearchState = useCallback(() => {
      setSelectedResearch([]);
      setSummaryStates({});
      setSummaryData({});
    }, []);

    // Computed values (exact same logic as workbook)
    const isAllSummariesComplete = useMemo(() => {
      if (selectedResearch.length === 0) return true;
      return selectedResearch.every(research => 
        summaryStates[research.id] === 'completed'
      );
    }, [selectedResearch, summaryStates]);

    const canProceedWithCreation = useMemo(() => {
      return isAllSummariesComplete;
    }, [isAllSummariesComplete]);

    const getExcludeIds = useCallback(() => {
      return selectedResearch.map(r => r.id);
    }, [selectedResearch]);

    const isAtLimit = useMemo(() => {
      return selectedResearch.length >= config.researchLimit;
    }, [selectedResearch]);

    const remainingSlots = useMemo(() => {
      return config.researchLimit - selectedResearch.length;
    }, [selectedResearch]);

    const contextValue: EntityCreationResearchContextType = {
      selectedResearch,
      addResearch,
      removeResearch,
      summaryStates,
      summaryData,
      isAllSummariesComplete,
      canProceedWithCreation,
      getExcludeIds,
      isAtLimit,
      remainingSlots,
      resetResearchState,
    };

    return (
      <EntityCreationContext.Provider value={contextValue}>
        {children}
      </EntityCreationContext.Provider>
    );
  };

  // Create entity-specific batch attach hook
  const useBatchAttachResearch = createBatchAttachHook(config);

  const system: EntityCreationResearchSystem = {
    Provider: EntityCreationProvider,
    useEntityCreationResearch,
    useBatchAttachResearch,
    config
  };

  // Cache the created system
  createdSystems[entityType] = system;

  return system;
};

// Convenience functions to get specific entity systems
export const getPageCreationResearchSystem = () => createEntityCreationResearchSystem('page');
export const getWorkbookCreationResearchSystem = () => createEntityCreationResearchSystem('workbook');
export const getProjectCreationResearchSystem = () => createEntityCreationResearchSystem('project'); 